import {
  Dropdown,
  ICON_ACCOUNT_CIRCLE_OUTLINE,
  Card,
  HStack,
  Badge,
  ICON_CHECK,
  ActionDropDown,
} from '@doveit/bricks';
import React from 'react';
import { UserActionsProps } from '../../../components/user-actions/UserActions';
import { Contact } from '../../../providers/api/dtos/contact';
import ContactInfo from '../contact-info/ContactInfo';
import useContactConcessions from '../../hooks/use-contact-concessions/useContactConcessions';
import { concessionLabels } from '../../../labels';
import UpdateContactAdditionalServicesAction from '../../containers/update-contact-additional-services-action/UpdateContactAdditionalServicesAction';
import UpdateContactOptInsAction from '../../containers/update-contact-opt-ins-action/UpdateContactOptInsAction';
import useIsDevice from '../../../hooks/use-is-device/useIsDevice';
import UpdateContactConcessionsAction from '../../containers/update-contact-concessions-action/UpdateContactConcessionsAction';
import UpdateContactAction from '../../containers/update-contact-action/UpdateContactAction';
import ViewContactAction from '../../containers/view-contact-action/ViewContactAction';

export interface ContactWidgetProps extends Pick<UserActionsProps, 'whatsAppMessage' | 'onCallActionClick' | 'onEmailActionClick' | 'onLinkedInActionClick'> {
  contact: Contact,
  title?: string,
  onUpdate?: (updatedContact: Contact) => void,
  canViewContact?: boolean,
  canEditContact?: boolean,
  canSeeAdditionalServices?: boolean,
  canAddAdditionalServices?: boolean,
  canDeleteAdditionalServices?: boolean,
  canSeeOptIns?: boolean,
  canAddOptIns?: boolean,
  canDeleteOptIns?: boolean,
  canSeeConcessions?: boolean,
  canAddConcessions?: boolean,
  canDeleteConcessions?: boolean,
  hideContactActions?: boolean,
}

const ContactWidget: React.FC<ContactWidgetProps> = ({
  contact,
  title = 'Contatto',
  onUpdate,
  canViewContact = false,
  canEditContact = false,
  canSeeAdditionalServices = false,
  canAddAdditionalServices = false,
  canDeleteAdditionalServices = false,
  canSeeConcessions = false,
  canAddConcessions = false,
  canDeleteConcessions = false,
  canSeeOptIns = false,
  canAddOptIns = false,
  canDeleteOptIns = false,
  hideContactActions = false,
  whatsAppMessage,
  onCallActionClick,
  onEmailActionClick,
  onLinkedInActionClick,
  ...rest
}) => {
  const isMobile = useIsDevice('mobile');

  const { data: concessions = [] } = useContactConcessions(contact?.id);

  return (
    <Card {...rest}>
      <Card.Header
        title={title}
        icon={{ path: ICON_ACCOUNT_CIRCLE_OUTLINE }}
        primaryActions={
          !isMobile && (
            canEditContact
            || canSeeAdditionalServices || canAddAdditionalServices || canDeleteAdditionalServices
            || canSeeConcessions || canAddConcessions || canDeleteConcessions
            || canSeeOptIns || canAddOptIns || canDeleteOptIns
          ) ? [
            <ActionDropDown
              label="Modifica"
              size="S"
              emphasis="low"
              aria-label="Azioni di modifica del contatto"
            >
              {canEditContact && (
                <UpdateContactAction
                  contact={contact}
                  onSuccess={onUpdate}
                >
                  {({ onClick }) => (
                    <Dropdown.Option
                      onClick={onClick}
                      label="Modifica contatto"
                      aria-label="Pulsante per modificare le informazioni del contatto"
                    />
                  )}
                </UpdateContactAction>
              )}
              {canSeeAdditionalServices && (
                <UpdateContactAdditionalServicesAction
                  contactId={contact.id!}
                  canAdd={canAddAdditionalServices}
                  canDelete={canDeleteAdditionalServices}
                >
                  {({ isSaving, update }) => (
                    <Dropdown.Option
                      onClick={update}
                      label="Modifica servizi"
                      loading={isSaving}
                    />
                  )}
                </UpdateContactAdditionalServicesAction>
              )}
              {canSeeOptIns && (
                <UpdateContactOptInsAction
                  contactId={contact.id!}
                  canAdd={canAddOptIns}
                  canDelete={canDeleteOptIns}
                >
                  {({ isSaving, update }) => (
                    <Dropdown.Option
                      onClick={update}
                      label="Modifica consensi"
                      loading={isSaving}
                    />
                  )}
                </UpdateContactOptInsAction>
              )}
              {canSeeConcessions && (
                <UpdateContactConcessionsAction
                  contactId={contact.id!}
                  canAdd={canAddConcessions}
                  canDelete={canDeleteConcessions}
                >
                  {({ isSaving, update }) => (
                    <Dropdown.Option
                      onClick={update}
                      label="Modifica agevolazioni"
                      loading={isSaving}
                    />
                  )}
                </UpdateContactConcessionsAction>
              )}
            </ActionDropDown>] : undefined
        }
        secondaryActions={[
          canViewContact && (
            <ViewContactAction contactId={contact.id!}>
              {({ onClick }) => (
                <Dropdown.Option
                  label="Visualizza"
                  aria-label="Visualizza contatto"
                  onClick={onClick}
                />
              )}
            </ViewContactAction>
          ),
          isMobile && canEditContact && (
            <UpdateContactAction
              contact={contact}
              onSuccess={onUpdate}
            >
              {({ onClick }) => (
                <Dropdown.Option
                  onClick={onClick}
                  label="Modifica contatto"
                  aria-label="Pulsante per modificare le informazioni del contatto"
                />
              )}
            </UpdateContactAction>
          ),
          isMobile && canSeeAdditionalServices && (
            <UpdateContactAdditionalServicesAction
              contactId={contact.id!}
              canAdd={canAddAdditionalServices}
              canDelete={canDeleteAdditionalServices}
            >
              {({ isSaving, update }) => (
                <Dropdown.Option
                  onClick={update}
                  label="Modifica servizi"
                  loading={isSaving}
                />
              )}
            </UpdateContactAdditionalServicesAction>
          ),
          isMobile && canSeeOptIns && (
            <UpdateContactOptInsAction
              contactId={contact.id!}
              canAdd={canAddOptIns}
              canDelete={canDeleteOptIns}
            >
              {({ isSaving, update }) => (
                <Dropdown.Option
                  onClick={update}
                  label="Modifica consensi"
                  loading={isSaving}
                />
              )}
            </UpdateContactOptInsAction>
          ),
          isMobile && canSeeConcessions && (
            <UpdateContactConcessionsAction
              contactId={contact.id!}
              canAdd={canAddConcessions}
              canDelete={canDeleteConcessions}
            >
              {({ isSaving, update }) => (
                <Dropdown.Option
                  onClick={update}
                  label="Modifica agevolazioni"
                  loading={isSaving}
                />
              )}
            </UpdateContactConcessionsAction>
          ),
        ]}
      />
      <Card.Content>
        <ContactInfo
          contact={contact}
          actions={{
            whatsAppMessage,
            onCallActionClick,
            onEmailActionClick,
            onLinkedInActionClick,
          }}
          hideActions={hideContactActions}
        />
      </Card.Content>
      {concessions.length > 0 && (
        <>
          <Card.Divider />

          <Card.Content>
            <HStack aria-label="Lista agevolazioni">
              {concessions.map(({ id: concessionId, type }) => (
                <Badge
                  key={concessionId}
                  icon={ICON_CHECK}
                  label={concessionLabels[type]}
                  size="XS"
                  color="primary"
                />
              ))}
            </HStack>
          </Card.Content>
        </>
      )}
    </Card>
  );
};

export default ContactWidget;
